import React from 'react'
import useDiscountStore from '@/context/DiscountStore'
import { validateDiscountCode, DiscountValidationResponse } from '@/services/discountApi'
import { formatPriceNumber } from '@/utils/priceFormatting'

const DiscountDebug: React.FC = () => {
    const { discountCode, setDiscountCode, setDiscountValidation, clearDiscount, calculateDiscountAmount, calculateFreeAddonDiscount, isAddonFree } = useDiscountStore()

    const testDiscountCode = async (code: string) => {
        setDiscountCode(code)
        try {
            const result: DiscountValidationResponse = await validateDiscountCode(code)
            setDiscountValidation({
                valid: result.valid,
                discountType: result.discountType,
                discountValue: result.discountValue,
                error: result.error || null,
                freeAddonIds: result.freeAddonIds,
                freeAddonTypes: result.freeAddonTypes
            })
        } catch (error) {
            setDiscountValidation({
                valid: false,
                error: 'Failed to validate discount code'
            })
        }
    }

    const testCodes = ['SAVE10', 'WELCOME5', 'NEWCUSTOMER5', 'FIXED20', 'BF-ENGRAVING25', 'EXPIRED', 'INVALID', 'promo_1RVU1PGbdV76TcwK5pkdW00p']
    const testSubtotal = 5000 // £50.00 in pence
    const mockBasketItems: any[] = [
        {
            extras: [
                {
                    _id: 'engraving_item_id',
                    productCatalogueId: '67519d69197065b8a54a327e',
                    price: { unit_amount: 500 } // £5 engraving
                }
            ]
        }
    ]

    if (process.env.NODE_ENV !== 'development') {
        return null
    }

    const testRetroactiveDiscount = async () => {
        console.log('🧪 Testing retroactive discount application...')

        // First clear any existing discount
        clearDiscount()
        console.log('1. Cleared existing discount')

        // Wait a moment for state to update
        await new Promise(resolve => setTimeout(resolve, 100))

        // Check if engraving is currently free (should be false)
        const beforeDiscount = isAddonFree('engraving_item_id', '67519d69197065b8a54a327e')
        console.log('2. Engraving free before discount:', beforeDiscount)

        // Apply BF-ENGRAVING25 discount
        await testDiscountCode('BF-ENGRAVING25')
        console.log('3. Applied BF-ENGRAVING25 discount')

        // Wait for validation to complete
        await new Promise(resolve => setTimeout(resolve, 1500))

        // Check if engraving is now free (should be true)
        const afterDiscount = isAddonFree('engraving_item_id', '67519d69197065b8a54a327e')
        console.log('4. Engraving free after discount:', afterDiscount)

        if (afterDiscount && !beforeDiscount) {
            console.log('✅ Retroactive discount application working!')
        } else {
            console.log('❌ Retroactive discount application failed')
        }
    }

    return (
        <div className="fixed bottom-4 left-4 bg-white p-4 rounded-lg shadow-lg border z-50 max-w-sm">
            <h3 className="font-bold mb-2">Discount Debug</h3>

            <div className="mb-4">
                <h4 className="font-semibold text-sm mb-2">Current State:</h4>
                {discountCode ? (
                    <div className="text-xs space-y-1">
                        <div>Code: {discountCode.code}</div>
                        <div>Valid: {discountCode.valid ? 'Yes' : 'No'}</div>
                        <div>Loading: {discountCode.isLoading ? 'Yes' : 'No'}</div>
                        <div>Type: {discountCode.discountType}</div>
                        {discountCode.valid && (
                            <>
                                <div>Value: {discountCode.discountValue}</div>
                                {discountCode.discountType === 'free_addon' ? (
                                    <>
                                        <div>Free Addon Discount: £{formatPriceNumber(calculateFreeAddonDiscount(mockBasketItems) / 100)}</div>
                                        <div>Is Engraving Free: {isAddonFree('engraving_item_id', '67519d69197065b8a54a327e') ? 'Yes' : 'No'}</div>
                                    </>
                                ) : (
                                    <div>Discount on £50: £{formatPriceNumber(calculateDiscountAmount(testSubtotal) / 100)}</div>
                                )}
                            </>
                        )}
                        {discountCode.error && (
                            <div className="text-red-500">Error: {discountCode.error}</div>
                        )}
                    </div>
                ) : (
                    <div className="text-xs text-gray-500">No discount code</div>
                )}
            </div>

            <div className="mb-4">
                <h4 className="font-semibold text-sm mb-2">Test Codes:</h4>
                <div className="space-y-1">
                    {testCodes.map(code => (
                        <button
                            key={code}
                            onClick={() => testDiscountCode(code)}
                            className="block w-full text-left px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                        >
                            <div className="font-semibold">{code}</div>
                            <div className="text-gray-600 text-[10px]">
                                {code === 'promo_1RVU1PGbdV76TcwK5pkdW00p' && '5% off'}
                                {code === 'WELCOME5' && '5% off'}
                                {code === 'NEWCUSTOMER5' && '5% off'}
                                {code === 'FIXED20' && '£20 off'}
                                {code === 'BF-ENGRAVING25' && 'Free engraving'}
                                {code === 'EXPIRED' && 'Expired code'}
                                {code === 'INVALID' && 'Invalid code'}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mb-2">
                <h4 className="font-semibold text-sm mb-2">Retroactive Test:</h4>
                <button
                    onClick={testRetroactiveDiscount}
                    className="w-full px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded text-xs text-blue-700 mb-2"
                >
                    Test Retroactive Free Engraving
                </button>
            </div>

            <div className="mb-2">
                <h4 className="font-semibold text-sm mb-2">URL Test:</h4>
                <div className="text-xs text-gray-600 mb-2">
                    Add ?code=BF-ENGRAVING25 to URL to test free engraving
                </div>
                <div className="text-xs text-gray-500 mb-2">
                    Try: ?code=NEWCUSTOMER5 for 5% off
                </div>
            </div>

            <div className="mb-2">
                <h4 className="font-semibold text-sm mb-2">Persistence:</h4>
                <div className="text-xs text-gray-600 mb-1">
                    Discount codes are saved to localStorage
                </div>
                <div className="text-xs text-gray-500 mb-2">
                    They persist across page refreshes and sessions
                </div>
            </div>

            <button
                onClick={clearDiscount}
                className="w-full px-2 py-1 bg-red-100 hover:bg-red-200 rounded text-xs text-red-700"
            >
                Clear Discount
            </button>
        </div>
    )
}

export default DiscountDebug 
